/**
 * Modules: what this release can add to the customer's app, and the one bit
 * they decide about each — on or off (MODULES §8.7).
 *
 * The catalogue *is* the current agent release, so there is nothing to browse
 * or install: a card appears because the release carries the module. Switching
 * one on changes what the *next* APK carries and nothing about the phones
 * already in the field, which is the sentence every card repeats.
 *
 * Nothing here knows a module by name (M6): every word on a card comes out of
 * the manifest the release brought with it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { ModuleOut } from "../api/types";
import { Empty, ErrorBox, Loading } from "../components/common";
import { permissionShortName } from "../design/modules";
import { useI18n, type Translate } from "../i18n";
import { localized } from "../i18n/localized";

/** The tooltip behind "adds 2 actions, 3 values": the ids themselves. */
function contributionList(module: ModuleOut, t: Translate): string {
  const lines: string[] = [];
  const actions = Object.keys(module.actions ?? {});
  const values = Object.keys(module.values ?? {});
  const blocks = Object.keys(module.blocks ?? {});
  const commands = Object.keys(module.commands ?? {});
  if (actions.length) lines.push(`${t("modules.actions")}: ${actions.join(", ")}`);
  if (values.length) lines.push(`${t("modules.values")}: ${values.join(", ")}`);
  if (blocks.length) lines.push(`${t("modules.blocks")}: ${blocks.join(", ")}`);
  if (commands.length) lines.push(`${t("modules.commands")}: ${commands.join(", ")}`);
  return lines.join("\n");
}

export default function Modules() {
  const { t, lang } = useI18n();
  const queryClient = useQueryClient();
  const [failed, setFailed] = useState<{ id: string; error: unknown } | null>(null);

  const modules = useQuery({ queryKey: ["modules"], queryFn: api.modules });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.setModuleEnabled(id, enabled),
    onMutate: ({ id }) => setFailed((last) => (last?.id === id ? null : last)),
    onSuccess: () => {
      // The catalogue carries `enabled` and `used_by`, and a design's warnings
      // move with it, so both lists are refetched rather than patched.
      queryClient.invalidateQueries({ queryKey: ["modules"] });
      queryClient.invalidateQueries({ queryKey: ["screen-designs"] });
    },
    onError: (error, { id }) => setFailed({ id, error }),
  });

  const name = (moduleId: string) =>
    localized(modules.data?.find((entry) => entry.id === moduleId)?.name, lang, moduleId);

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h2>{t("modules.title")}</h2>
          <p className="page-sub">{t("modules.intro")}</p>
        </div>
      </header>

      {modules.isLoading && <Loading what={t("modules.title")} />}
      {modules.error && <ErrorBox error={modules.error} />}
      {modules.data?.length === 0 && (
        <section className="card">
          <Empty>{t("modules.emptyRelease")}</Empty>
        </section>
      )}

      <div className="module-cards">
        {(modules.data ?? []).map((module) => {
          const busy = toggle.isPending && toggle.variables?.id === module.id;
          const error = failed?.id === module.id ? failed.error : null;
          const requires =
            error instanceof ApiError && error.code === "module_requires"
              ? String(error.detail?.module ?? "")
              : null;

          return (
            <section className="card module-card" key={module.id}>
              <header className="module-head">
                <div>
                  <h3>{localized(module.name, lang, module.id)}</h3>
                  <p className="muted small mono">
                    {module.id} {module.version}
                  </p>
                </div>
                <label className="module-toggle">
                  <input
                    type="checkbox"
                    checked={module.enabled}
                    disabled={busy || !module.available}
                    aria-label={t("modules.toggleAria", {
                      module: localized(module.name, lang, module.id),
                    })}
                    onChange={(event) =>
                      toggle.mutate({ id: module.id, enabled: event.target.checked })
                    }
                  />
                  <span>{module.enabled ? t("modules.on") : t("modules.off")}</span>
                </label>
              </header>

              <p>{localized(module.description, lang)}</p>

              <p className="muted small" title={contributionList(module, t)}>
                {t("modules.adds", {
                  actions: module.counts.actions,
                  values: module.counts.values,
                  blocks: module.counts.blocks,
                  commands: module.counts.commands,
                })}
                {module.size_kb !== null && ` · ${t("modules.size", { kb: module.size_kb })}`}
              </p>

              {module.permissions.length > 0 && (
                <ul className="module-permissions">
                  {module.permissions.map((permission) => (
                    <li key={permission.name}>
                      <strong>{permissionShortName(permission.name)}</strong>
                      {/* An optional permission never holds the module at
                          `needs_permission` (MODULES §2.5), so a fleet that
                          refuses it still reads as working. */}
                      {permission.optional && (
                        <span
                          className="module-optional"
                          title={t("modules.permissionOptionalHint")}
                        >
                          {t("modules.permissionOptional")}
                        </span>
                      )}
                      {permission.reason && <> — {localized(permission.reason, lang)}</>}
                      <span className="muted small mono"> {permission.name}</span>
                    </li>
                  ))}
                </ul>
              )}

              {!module.available && (
                <p className="error-text small">
                  {t("modules.needsAgent", { version: module.min_agent_version ?? "—" })}
                </p>
              )}

              {module.used_by.length > 0 && (
                <p className="small">
                  {t("modules.usedBy")}{" "}
                  {module.used_by.map((use, index) => (
                    <span key={use.design_id}>
                      {index > 0 && ", "}
                      <Link to={`/design/${use.design_id}`}>{use.name}</Link>
                    </span>
                  ))}
                  {!module.enabled && (
                    <span className="error-text"> — {t("modules.usedByWhileOff")}</span>
                  )}
                </p>
              )}

              {requires ? (
                <p className="error-text small">
                  {t("modules.requires", { module: name(requires) })}
                </p>
              ) : (
                error != null && <ErrorBox error={error} />
              )}

              <p className="muted small">{t("modules.apkNote")}</p>
            </section>
          );
        })}
      </div>
    </div>
  );
}
